// apps/client/src/engine/shader/toyShading.ts (light and look, 2026-10-16) — the soft toy finish in three's own lighting chunks, so every
// lit material in the town, the house interiors, the museum, the residents and the item icons gets it without a patch of its own:
//   wrap — the key light reaches a little past the terminator (tuning.light.shading.wrap), so a rounded form turns softly into the shade;
//   rim  — a faint sheen in the hemisphere's sky colour where a form turns away from the camera, so its silhouette reads against the
//          ground; it follows the clock with the fill (warm at golden hour, blue at night).
// The chunks are global: installToyShading() runs before anything compiles (createRenderer, the icon renderer) and is idempotent.
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';

const S = tuning.light.shading;
const f = (n: number) => n.toFixed(4);
const DIRECT = 'float dotNL = saturate( dot( geometryNormal, directLight.direction ) );';
const WRAP_FN = `#define TOY_WRAP ${f(S.wrap)}
float toyWrap( const in float nl ) { return saturate( ( nl + TOY_WRAP ) / ( 1.0 + TOY_WRAP ) ); }
`;
const RIM = `
	#if NUM_HEMI_LIGHTS > 0
		float toyRim = pow( 1.0 - saturate( dot( geometryNormal, geometryViewDir ) ), ${f(S.rimPower)} ) * ${f(S.rim)};
		irradiance += hemisphereLights[ 0 ].skyColor * toyRim;
	#endif
`;
const INDIRECT = 'RE_IndirectDiffuse( irradiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );';
const MARK = '// toy shading';

/** The same wrap on the CPU (tests): n·l → wrapped diffuse factor. */
export function toyWrap(nl: number, wrap: number = S.wrap): number {
  return Math.min(1, Math.max(0, (nl + wrap) / (1 + wrap)));
}

function replaceOnce(src: string, find: string, put: string, name: string): string {
  const i = src.indexOf(find);
  if (i < 0) throw new Error(`toyShading: ${name} no longer has the expected source (three upgraded?)`);
  return src.slice(0, i) + put + src.slice(i + find.length);
}

/** The wrap reaches the direct light's diffuse only. The specular terms keep the plain n·l: GGX's visibility term grows without bound
 *  where n·l and n·v both reach 0, which a wrapped (non-zero) irradiance there turned into white speckles along every silhouette turned
 *  away from the sun. */
const WRAPPED = 'vec3 toyIrradiance = toyWrap( dot( geometryNormal, directLight.direction ) ) * directLight.color;';
const DIFFUSE = 'reflectedLight.directDiffuse += irradiance * BRDF_Lambert(';
function wrapDirectDiffuse(src: string, name: string): string {
  const at = src.indexOf(DIRECT);
  if (at < 0) throw new Error(`toyShading: ${name} no longer has the expected source (three upgraded?)`);
  const head = src.slice(0, at + DIRECT.length), rest = src.slice(at + DIRECT.length);
  // The first direct diffuse after the direct light's n·l is RE_Direct's own (the indirect one reads `indirectDiffuse`).
  return `${head}\n\t${WRAPPED}${replaceOnce(rest, DIFFUSE, 'reflectedLight.directDiffuse += toyIrradiance * BRDF_Lambert(', name)}`;
}

export function installToyShading(): void {
  const C = THREE.ShaderChunk as unknown as Record<string, string>;
  if (C.lights_fragment_end!.includes(MARK)) return;
  for (const name of ['lights_physical_pars_fragment', 'lights_lambert_pars_fragment', 'lights_phong_pars_fragment']) {
    C[name] = `${MARK}\n${WRAP_FN}${wrapDirectDiffuse(C[name]!, name)}`;
  }
  C.lights_fragment_end = `${MARK}\n${replaceOnce(C.lights_fragment_end!, INDIRECT, `${RIM}\n\t${INDIRECT}`, 'lights_fragment_end')}`;
}
